import React, { useState, useEffect } from "react";
import { useI18n } from "Hooks";
import _ from "lodash";
import {
  getDateMomentObjectCustom,
  getDateMomentObject,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  CreateEventButton,
  UpdateEventButton,
} from "MyCalendar/components/EventModalV2/components/EventActionButton";
import { addTimeToDate } from "../modules/Utils";
import { getUserInfo } from "Login/modules/LoginModule";

const useEventModal = props => {
  const {
    onCloseEventCreationModal,
    onCloseEventDetailsModal,
    showModal,
    mode,
    resetEventModalV2,
    selectedTimeSlot = null,
    updateEventModalV2,
    eventModalV2,
    selectedAcademicYearStartDate,
    openedFromHeader,
  } = props;
  const userInfo = getUserInfo();
  const isDevUser = userInfo?.isDevUser;
  const { t } = useI18n(["calendar", "common"]);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const isEditMode = _.toLower(mode) === "edit";
  const isCreateMode = _.toLower(mode) === "create";
  const isFullScreenMode = _.includes(["edit", "duplicate"], _.toLower(mode));
  const [isFullScreenModal, setIsFullScreenModal] = useState(isFullScreenMode);
  const { isFormUpdated, isVideoMeetingFormUpdated } = eventModalV2;
  const closeModal = () => {
    if (isFormUpdated || isVideoMeetingFormUpdated) {
      setIsConfirmationModalOpen(true);
    } else {
      onDiscardingChanges();
    }
  };

  const onDiscardingChanges = () => {
    setIsConfirmationModalOpen(false);
    setIsModalOpen(false);
    setIsFullScreenModal(false);
    if (_.isFunction(onCloseEventCreationModal)) {
      onCloseEventCreationModal();
    }
    if (_.isFunction(onCloseEventDetailsModal)) {
      onCloseEventDetailsModal();
    }
    if (_.isFunction(resetEventModalV2)) {
      resetEventModalV2();
    }
    // Additional logic to reset the modal state can be added here
  };

  const onCancelDiscardingChanges = () => {
    setIsConfirmationModalOpen(false);
  };

  const openFullScreenModal = () => {
    setIsFullScreenModal(true);
  };

  const closeFullScreenModal = () => {
    setIsFullScreenModal(false);
    if (_.isFunction(resetEventModalV2)) {
      resetEventModalV2();
    }
  };

  useEffect(() => {
    if (!isCreateMode) return;
    // Reset the startDate and endDate if selectedTimeSlot is empty

    const academicYearStartDate = getDateMomentObject(
      selectedAcademicYearStartDate
    );
    // this is doing to ensure that the startDate and endDate are not before the academic year start date
    // we can create event in future academic year as well in dp curriculam
    const maxDateTime = academicYearStartDate.isAfter(getDateMomentObject())
      ? addTimeToDate(
          getDateMomentObject(academicYearStartDate),
          getDateMomentObject()
        )
      : getDateMomentObject();

    if (!_.isEmpty(selectedTimeSlot)) {
      const { startDate, endDate, startTime, endTime } = selectedTimeSlot || {};
      const startDateTime = getDateMomentObjectCustom(
        `${startDate} ${startTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm
      );
      const endDateTime = getDateMomentObjectCustom(
        `${endDate} ${endTime}`,
        DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm
      );

      updateEventModalV2({
        startDate: openedFromHeader ? maxDateTime : startDateTime,
        endDate: openedFromHeader
          ? maxDateTime.clone().add(30, "minutes")
          : endDateTime,
      });
    } else {
      updateEventModalV2({
        startDate: maxDateTime,
        endDate: maxDateTime,
      });
    }
  }, []);

  const actionButtons = {
    edit: (
      <UpdateEventButton
        onClick={onDiscardingChanges}
        setShowFullScreenLoader={setShowFullScreenLoader}
      />
    ),
    create: (
      <CreateEventButton
        onClick={onDiscardingChanges}
        setShowFullScreenLoader={setShowFullScreenLoader}
      />
    ),
    duplicate: (
      <CreateEventButton
        onClick={onDiscardingChanges}
        label={t("calendar:duplicate_event")}
        setShowFullScreenLoader={setShowFullScreenLoader}
      />
    ),
  };

  return {
    t,
    isModalOpen,
    setIsModalOpen,
    isFullScreenModal,
    setIsFullScreenModal,
    closeModal,
    openFullScreenModal,
    showModal,
    mode: _.toLower(mode),
    closeFullScreenModal,
    isConfirmationModalOpen,
    onDiscardingChanges,
    onCancelDiscardingChanges,
    isEditMode,
    actionButtons,
    showFullScreenLoader,
    isCreateMode,
    isDevUser,
    isRepeatEvent: eventModalV2?.isRepeatEvent || false,
    setShowFullScreenLoader,
  };
};

export default useEventModal;
